import { NavLink, Outlet, Navigate, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, CalendarRange, Users, Wallet, Send, Tags, MapPin, GraduationCap, Ship, Handshake, Newspaper, Megaphone, Languages, FileStack, LogOut, Globe, Inbox, UserRound, ShoppingCart, TicketPercent, UserCircle, FileText, Receipt, Building2 } from "lucide-react";
import { useAuth } from "../../lib/auth";
import { Logo } from "../../components/site/Logo";
import { ThemeToggle } from "../../lib/theme";
import { HelpButton } from "../../components/site/HelpButton";

const NAV = [
  ["Overview", [["", LayoutDashboard, "Dashboard"], ["profile", UserCircle, "My profile"]]],
  ["Operations", [["events", CalendarRange, "Events"], ["registrants", Users, "Registrants"], ["users", UserRound, "Users"], ["payments", Wallet, "Payments"], ["orders", ShoppingCart, "Enrolments"], ["discounts", TicketPercent, "Discounts"], ["communications", Send, "Communications"], ["requests", Inbox, "Contact requests"]]],
  ["Administration", [["proforma", FileText, "Proforma"], ["invoices", Receipt, "Invoices"], ["companies", Building2, "Companies"]]],
  ["Registry", [["classes", Tags, "Classes"], ["locations", MapPin, "Locations"], ["instructors", GraduationCap, "Instructors"], ["charters", Ship, "Charter"]]],
  ["Public site", [["content", FileStack, "Pages & defaults"], ["news", Newspaper, "News"], ["partners", Handshake, "Partnership"], ["popups", Megaphone, "Popups"], ["translations", Languages, "Translations"], ["templates", Send, "Email templates"]]],
];

export default function AdminLayout() {
  const { user, signOut } = useAuth();
  const { pathname } = useLocation();
  if (user === null) return <div className="h-screen" />;
  if (!user || !["admin", "staff", "collaborator"].includes(user.role)) return <Navigate to="/login?next=/admin" />;
  const staff = user.role === "staff";
  if (staff && pathname !== "/admin/registrants") return <Navigate to="/admin/registrants" replace />;
  const collab = user.role === "collaborator";
  if (collab && !["/admin/proforma", "/admin/invoices"].includes(pathname)) return <Navigate to="/admin/proforma" replace />;
  const nav = staff ? [["Staff", [["registrants", Users, "Registrants"]]]] : collab ? [["Administration", NAV.find((g) => g[0] === "Administration")[1].filter((i) => i[0] !== "companies")]] : NAV;
  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-[#071a2d]" data-testid="admin-layout">
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col overflow-y-auto bg-[#0A2540] px-4 py-6 text-white lg:flex">
        <Logo light className="px-2" />
        <nav className="mt-8 flex-1 space-y-6">
          {nav.map(([group, items]) => (
            <div key={group}>
              <p className="px-3 text-[10px] font-bold uppercase tracking-[0.25em] text-white/35">{group}</p>
              <div className="mt-2 space-y-0.5">
                {items.map(([to, Icon, label]) => (
                  <NavLink key={label} to={`/admin${to ? `/${to}` : ""}`} end={!to} data-testid={`admin-nav-${to || "dashboard"}`}
                    className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition-colors ${isActive ? "bg-white/10 text-white" : "text-white/60 hover:text-white"}`}>
                    <Icon size={16} />{label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="space-y-1 border-t border-white/10 pt-4">
          <Link to="/" className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm text-white/60 hover:text-white" data-testid="admin-view-site"><Globe size={16} />View site</Link>
          <div className="px-3 py-2"><ThemeToggle light /></div>
          <button onClick={signOut} className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm text-white/60 hover:text-white" data-testid="admin-logout"><LogOut size={16} />Logout</button>
        </div>
      </aside>
      <div className="min-w-0 flex-1">
        <div className="sticky top-0 z-40 bg-[#0A2540] lg:hidden" data-testid="admin-mobile-bar">
          <div className="flex items-center justify-between gap-3 px-4 py-3">
            <Logo light />
            <div className="flex items-center gap-3">
              <Link to="/" className="text-white/70 hover:text-white" title="View site" data-testid="admin-mobile-view-site"><Globe size={18} /></Link>
              <button onClick={signOut} className="inline-flex items-center gap-1.5 rounded-full border border-white/25 px-3 py-1.5 text-xs font-semibold text-white" data-testid="admin-mobile-logout"><LogOut size={14} />Logout</button>
            </div>
          </div>
          <div className="flex gap-2 overflow-x-auto px-3 pb-3">
            {nav.flatMap(([, i]) => i).map(([to, , label]) => <NavLink key={label} to={`/admin${to ? `/${to}` : ""}`} end={!to} className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1 text-xs ${isActive ? "bg-white/15 text-white" : "text-white/80"}`}>{label}</NavLink>)}
          </div>
        </div>
        <main className="p-4 sm:p-6 lg:p-10"><Outlet /></main>
      </div>
    </div>
  );
}

export const AdminH = ({ title, sub, children }) => (
  <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
    <div><h1 className="font-display text-3xl font-extrabold text-[#0A2540]">{title}</h1>{sub && <p className="mt-1 text-sm text-slate-500">{sub}</p>}</div>
    <div className="flex flex-wrap items-center gap-2">{children}<HelpButton /></div>
  </div>
);
